import { useSelector } from 'react-redux'

import type { AlternativeOptions } from '@features/recipe-tile'
import type { Recipe } from '@library/menu-service'
import type { MenuRecipe } from '@library/menu-service/queries/menuApiMenuRecipe/types'

import { useBasketRecipes } from 'hooks/basket/useBasketRecipes'
import { useCollection, useCurrentCollectionId } from 'hooks/menu-collections'
import { useRecipes } from 'hooks/menu-recipes/useRecipes'
import { useFetchRecipe } from 'routes/Menu/components/Detail/hooks/useFetchRecipe'
import { getBasketDate, getBasketMenuUuid, getNumPortions } from 'selectors/basket'

const compareCoreRecipeIds = (a: Recipe, b: Recipe) =>
  parseInt(a.coreId, 10) - parseInt(b.coreId, 10)

const renderRecipeName = (recipe: Recipe) =>
  [
    recipe.name,
    recipe.dietaryClaims.some((claim) => claim.slug === 'vegetarian' || claim.slug === 'vegan')
      ? '(V)'
      : undefined,
  ]
    .filter((part) => Boolean(part))
    .join(' ')

export const useRecipeOptions = (
  mainRecipe: Recipe,
  shouldFetchOptions: boolean,
): {
  isLoadingOptions: boolean
  alternativeOptions: AlternativeOptions
} => {
  const { basketHasRecipe } = useBasketRecipes()

  const numPortions = useSelector(getNumPortions)
  const deliveryDate = useSelector(getBasketDate)
  const menuId = useSelector(getBasketMenuUuid)
  const collectionId = useCurrentCollectionId()
  const collection = useCollection(collectionId)

  const baseRecipe =
    collection?.recipes.find((recipe) => recipe.id === mainRecipe.id) ||
    collection?.recipes.find((recipe) =>
      recipe.options.some((option) => option.id === mainRecipe.id),
    )

  const optionsType = baseRecipe?.optionsType || 'none'

  const {
    loading,
    recipe: menuRecipe,
    error,
  } = useFetchRecipe(
    menuId,
    mainRecipe.id,
    numPortions,
    deliveryDate,
    shouldFetchOptions,
    collectionId,
  )

  // @todo this should also be dealing with RecipeQueryResponse when we switch to the new API
  const fullRecipe = menuRecipe as MenuRecipe

  const menuRecipes: Recipe[] = useRecipes(
    fullRecipe ? fullRecipe.options.map((recipe) => recipe.item_id) : [],
  ).filter((recipe): recipe is Recipe => recipe !== null)

  if (!fullRecipe || error) {
    return {
      isLoadingOptions: loading,
      alternativeOptions: {
        optionsType,
        options: null,
      },
    }
  }

  const orderedOptions = menuRecipes.sort(compareCoreRecipeIds)

  const options = orderedOptions.map((recipe) => ({
    recipe,
    recipeId: recipe.coreId,
    recipeName: renderRecipeName(recipe),
    isChecked: mainRecipe.id === recipe.id,
    isOutOfStock: !recipe.isAvailable && !basketHasRecipe(recipe.coreId),
    surcharge: recipe.surcharge ? recipe.surcharge.pricePerPortion / 100 : null,
    ingredientSlotValues: fullRecipe.options?.find((option) => option.item_id === recipe.id)
      ?.ingredient_slot_values,
  }))

  return {
    isLoadingOptions: loading,
    alternativeOptions: {
      ingredientSlots: fullRecipe.ingredient_slots,
      options,
      optionsType: options.length > 0 ? fullRecipe.options_type : 'none',
    },
  }
}
